import { useState, useEffect, useMemo, useCallback } from "react";
import { supabaseAdmin as supabase } from "@/lib/supabaseAdmin";

interface WithdrawalRow {
  id: string;
  user_id: string;
  amount: number;
  type: string;
  bank_name: string | null;
  bank_account: string | null;
  branch_code: string | null;
  account_holder: string | null;
  user_name: string | null;
  status: string;
  created_at: string;
}

interface ProfileMap {
  [id: string]: { name: string; phone: string };
}

/* ── Status display mapping (兼容舊英文 + 新中文) ── */
const STATUS_DISPLAY: Record<string, { text: string; style: string }> = {
  pending: { text: "待審核", style: "bg-amber-50 text-amber-600 border-amber-200" },
  approved: { text: "已核准", style: "bg-emerald-50 text-emerald-600 border-emerald-200" },
  "已完成": { text: "已完成", style: "bg-emerald-50 text-emerald-600 border-emerald-200" },
  rejected: { text: "已拒絕", style: "bg-red-50 text-red-500 border-red-200" },
  "已拒絕": { text: "已拒絕", style: "bg-red-50 text-red-500 border-red-200" },
};

function getStatusDisplay(status: string | null) {
  return STATUS_DISPLAY[status || ""] || { text: status || "未知", style: "bg-stone-50 text-stone-500 border-stone-200" };
}

export default function AdminFinance() {
  const [withdrawals, setWithdrawals] = useState<WithdrawalRow[]>([]);
  const [profiles, setProfiles] = useState<ProfileMap>();
  const [loading, setLoading] = useState(true);
  const [actionMsg, setActionMsg] = useState("");
  const [activeFilter, setActiveFilter] = useState<"all" | "pending" | "approved" | "rejected">("all");
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<"已完成" | "已拒絕" | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);

  // 穩定 fetchData：useCallback + 空依賴，只執行一次
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [wdRes, profilesRes] = await Promise.all([
        supabase.from("withdrawals").select("id, user_id, amount, type, bank_name, bank_account, branch_code, account_holder, user_name, status, created_at").order("created_at", { ascending: false }).limit(20),
        supabase.from("profiles").select("id, name, phone").limit(100),
      ]);

      if (wdRes.error) {
        console.error("withdrawals fetch error:", wdRes.error);
      } else {
        setWithdrawals((wdRes.data as WithdrawalRow[]) || []);
      }

      if (profilesRes.error) {
        console.error("profiles fetch error:", profilesRes.error);
      } else {
        const map: ProfileMap = {};
        (profilesRes.data || []).forEach((p: { id: string; name: string | null; phone: string | null }) => {
          map[p.id] = { name: p.name || "未知用戶", phone: p.phone || "—" };
        });
        setProfiles(map);
      }
    } catch (err) {
      console.error("fetchData exception:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleStatus = async (id: string, status: "已完成" | "已拒絕") => {
    setProcessingId(id);
    setActionMsg("");

    try {
      // Step 1: 先讀取該筆提款的當前資訊（防重複操作 + 取得 user_id/amount）
      const { data: currentWd, error: fetchError } = await supabase
        .from("withdrawals")
        .select("user_id, amount, status")
        .eq("id", id)
        .single();

      if (fetchError || !currentWd) {
        setActionMsg(`讀取提款資料失敗：${fetchError?.message || "紀錄不存在"}`);
        return;
      }

      if (currentWd.status !== "pending") {
        setActionMsg("該筆申請已處理過，無法重複操作");
        await fetchData();
        return;
      }

      // Step 2: 真實寫入 withdrawals status
      const { error: updateError } = await supabase
        .from("withdrawals")
        .update({ status, updated_at: new Date().toISOString() })
        .eq("id", id);

      if (updateError) {
        setActionMsg(`更新失敗：${updateError.message}`);
        return;
      }

      // Step 3: 若是拒絕，自動退款至會員錢包 + 寫流水帳
      if (status === "已拒絕") {
        // 3a: 讀取當前錢包餘額
        const { data: walletData, error: walletError } = await supabase
          .from("wallets")
          .select("balance")
          .eq("user_id", currentWd.user_id)
          .single();

        if (walletError || !walletData) {
          setActionMsg(`錢包讀取失敗：${walletError?.message || "未知錯誤"}`);
          return;
        }

        // 3b: 加回餘額
        const newBalance = walletData.balance + currentWd.amount;
        const { error: walletUpdateError } = await supabase
          .from("wallets")
          .update({ balance: newBalance, updated_at: new Date().toISOString() })
          .eq("user_id", currentWd.user_id);

        if (walletUpdateError) {
          setActionMsg(`退款失敗：${walletUpdateError.message}`);
          return;
        }

        // 3c: 寫入流水帳
        const { error: txError } = await supabase
          .from("wallet_transactions")
          .insert({
            user_id: currentWd.user_id,
            amount: currentWd.amount,
            type: "withdrawal_rejected",
            description: "提領駁回退款",
            created_at: new Date().toISOString(),
          });

        if (txError) {
          setActionMsg(`流水帳寫入失敗：${txError.message}`);
          return;
        }
      }

      // Step 4: 強制重新讀取，確保刷新後不回彈
      await fetchData();
      setActionMsg(
        `已${status === "已完成" ? "核准" : "拒絕"}該筆申請${
          status === "已拒絕" ? "，並已自動退款至會員錢包" : ""
        }`
      );
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "更新失敗，請重試";
      setActionMsg(`更新失敗：${msg}`);
    } finally {
      setProcessingId(null);
      setConfirmingId(null);
      setConfirmAction(null);
      setTimeout(() => setActionMsg(""), 4000);
    }
  };

  const pending = useMemo(() => withdrawals.filter((w) => w.status === "pending"), [withdrawals]);
  const approved = useMemo(() => withdrawals.filter((w) => w.status === "approved" || w.status === "已完成"), [withdrawals]);
  const rejected = useMemo(() => withdrawals.filter((w) => w.status === "rejected" || w.status === "已拒絕"), [withdrawals]);
  const totalAmount = useMemo(() => pending.reduce((s, w) => s + w.amount, 0), [pending]);

  const filtered =
    activeFilter === "all"
      ? withdrawals
      : activeFilter === "pending"
        ? pending
        : activeFilter === "approved"
          ? approved
          : rejected;

  return (
    <div className="flex flex-col gap-4">
      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-stone-200 p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center">
              <i className="ri-time-line text-amber-600 text-lg" />
            </div>
            <span className="font-body text-[11px] text-stone-400">待處理</span>
          </div>
          <p className="font-display text-2xl font-bold text-stone-900">{loading ? "—" : pending.length}</p>
          <p className="font-body text-xs text-stone-400 mt-1">筆提現待審核</p>
        </div>
        <div className="bg-white rounded-xl border border-stone-200 p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center">
              <i className="ri-money-cny-circle-line text-red-500 text-lg" />
            </div>
            <span className="font-body text-[11px] text-stone-400">待審核總額</span>
          </div>
          <p className="font-display text-2xl font-bold text-stone-900">{loading ? "—" : `NT$ ${totalAmount.toLocaleString()}`}</p>
          <p className="font-body text-xs text-stone-400 mt-1">累計待放行金額</p>
        </div>
        <div className="bg-white rounded-xl border border-stone-200 p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center">
              <i className="ri-check-double-line text-emerald-600 text-lg" />
            </div>
            <span className="font-body text-[11px] text-stone-400">已核准</span>
          </div>
          <p className="font-display text-2xl font-bold text-stone-900">{loading ? "—" : approved.length}</p>
          <p className="font-body text-xs text-stone-400 mt-1">筆已放行</p>
        </div>
        <div className="bg-white rounded-xl border border-stone-200 p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="w-9 h-9 rounded-lg bg-stone-100 flex items-center justify-center">
              <i className="ri-close-circle-line text-stone-500 text-lg" />
            </div>
            <span className="font-body text-[11px] text-stone-400">已拒絕</span>
          </div>
          <p className="font-display text-2xl font-bold text-stone-900">{loading ? "—" : rejected.length}</p>
          <p className="font-body text-xs text-stone-400 mt-1">筆已退回</p>
        </div>
      </div>

      {actionMsg && (
        <div className={`font-body text-sm px-4 py-2.5 rounded-lg ${
          actionMsg.includes("失敗") || actionMsg.includes("無法")
            ? "bg-red-50 text-red-600 border border-red-200"
            : "bg-emerald-50 text-emerald-700 border border-emerald-200"
        }`}>
          {actionMsg}
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-2 bg-white rounded-lg border border-stone-200 p-1 w-fit">
        {[
          { key: "all", label: "全部", count: withdrawals.length },
          { key: "pending", label: "待審核", count: pending.length },
          { key: "approved", label: "已核准", count: approved.length },
          { key: "rejected", label: "已拒絕", count: rejected.length },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveFilter(tab.key as typeof activeFilter)}
            className={`flex items-center gap-1.5 font-body text-xs font-medium px-3 py-1.5 rounded-md whitespace-nowrap cursor-pointer ${
              activeFilter === tab.key
                ? "bg-stone-800 text-white"
                : "text-stone-500 hover:text-stone-700 hover:bg-stone-50"
            }`}
          >
            {tab.label}
            <span className={`font-body text-[10px] px-1 py-0.5 rounded-full min-w-[18px] text-center ${
              activeFilter === tab.key ? "bg-white/20 text-white" : "bg-stone-100 text-stone-500"
            }`}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-stone-200 bg-stone-50/50">
                {["申請人", "金額", "類型", "銀行帳戶", "申請日期", "狀態", "操作"].map((h) => (
                  <th key={h} className="font-body text-[11px] font-semibold text-stone-600 text-left px-4 py-3 whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {loading && (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <p className="font-body text-sm text-stone-400">載入中...</p>
                  </td>
                </tr>
              )}

              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <p className="font-body text-sm text-stone-400">暫無財務申請資料</p>
                  </td>
                </tr>
              )}

              {!loading && filtered.map((w) => {
                const user = profiles?.[w.user_id] || { name: w.user_name || "未知用戶", phone: "—" };
                const bankDisplay = w.bank_name
                  ? `${w.bank_name} ${w.branch_code ? `(${w.branch_code})` : ""} / ${w.bank_account || ""} / ${w.account_holder || ""}`
                  : "—";
                const isProcessing = processingId === w.id;
                const statusDisp = getStatusDisplay(w.status);
                return (
                  <tr key={w.id}>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-body text-sm font-medium text-stone-900">{user.name}</p>
                        <p className="font-body text-[10px] text-stone-400">{user.phone}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-body text-sm font-bold text-stone-900">NT$ {w.amount.toLocaleString()}</td>
                    <td className="px-4 py-3 font-body text-xs text-stone-600">{w.type === "withdrawal" ? "提現" : "入金"}</td>
                    <td className="px-4 py-3 font-body text-xs text-stone-500 max-w-[200px] truncate" title={bankDisplay}>
                      {bankDisplay}
                    </td>
                    <td className="px-4 py-3 font-body text-xs text-stone-500">{w.created_at?.slice(0, 10) || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`font-body text-[10px] font-semibold px-2 py-1 rounded-full border ${statusDisp.style}`}>
                        {statusDisp.text}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {w.status === "pending" && (
                        <div className="flex items-center gap-1">
                          {confirmingId === w.id && confirmAction ? (
                            <div className="flex items-center gap-1">
                              <span className="font-body text-[10px] text-stone-500 mr-1">
                                確認{confirmAction === "已完成" ? "核准" : "拒絕"}?
                              </span>
                              <button
                                onClick={() => handleStatus(w.id, confirmAction)}
                                disabled={isProcessing}
                                className={`font-body text-[10px] font-semibold px-2 py-1 rounded-md text-white cursor-pointer ${
                                  isProcessing ? "bg-emerald-300 cursor-not-allowed" : "bg-emerald-500 hover:bg-emerald-600"
                                }`}
                              >
                                {isProcessing ? (
                                  <span className="flex items-center gap-1">
                                    <i className="ri-loader-4-line animate-spin" />
                                    處理
                                  </span>
                                ) : (
                                  "確認"
                                )}
                              </button>
                              <button
                                onClick={() => { setConfirmingId(null); setConfirmAction(null); }}
                                disabled={isProcessing}
                                className="font-body text-[10px] px-2 py-1 rounded-md border border-stone-200 text-stone-500 hover:bg-stone-50 cursor-pointer"
                              >
                                取消
                              </button>
                            </div>
                          ) : (
                            <>
                              <button
                                onClick={() => { setConfirmingId(w.id); setConfirmAction("已完成"); }}
                                className="font-body text-[10px] font-semibold px-2 py-1 rounded-md bg-emerald-500 text-white hover:bg-emerald-600 cursor-pointer"
                              >
                                核准
                              </button>
                              <button
                                onClick={() => { setConfirmingId(w.id); setConfirmAction("已拒絕"); }}
                                className="font-body text-[10px] font-semibold px-2 py-1 rounded-md border border-red-300 text-red-500 hover:bg-red-50 cursor-pointer"
                              >
                                拒絕
                              </button>
                            </>
                          )}
                        </div>
                      )}
                      {w.status !== "pending" && (
                        <span className="font-body text-[10px] text-stone-400">
                          {w.status === "approved" || w.status === "已完成" ? "已處理" : "已退回"}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}